import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { fmtNumber } from "../lib/utils";
import {
  Upload as UpIcon, FileSpreadsheet, Check, Sparkles, Download, AlertTriangle,
  Bot, Database, Filter, ChevronDown, ChevronUp, X, RefreshCcw, Trash2, Flag,
} from "lucide-react";

const STEPS = ["Upload CSV", "Review AI Mapping", "Commit"];
const CONF_BAND = (c) => c >= 0.85 ? "high" : c >= 0.65 ? "medium" : "low";
const BAND_PILL = { high: "pill-success", medium: "pill-warn", low: "pill-error" };

export default function Upload() {
  const [step, setStep] = useState(0);
  const [file, setFile] = useState(null);
  const [useAi, setUseAi] = useState(false);
  const [preview, setPreview] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ kpi: "", band: "", search: "", onlyAccepted: false, view: "all" });
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [committed, setCommitted] = useState(null);
  const [catalogue, setCatalogue] = useState(null);

  useEffect(() => {
    api.get("/activity-records/catalogue").then((r) => setCatalogue(r.data));
  }, []);

  const onUpload = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("use_ai", String(useAi));
      const r = await api.post("/uploads/activity-csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setRows(r.data.rows);
      setStep(1);
    } catch (e) { setErr(e?.response?.data?.detail || "Preview failed"); }
    finally { setBusy(false); }
  };

  const requestAiAssist = async () => {
    if (!file) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("use_ai", "true");
      const r = await api.post("/uploads/activity-csv/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setRows(r.data.rows);
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/uploads/activity-csv/commit", { rows });
      setCommitted(r.data);
      setStep(2);
    } catch (e) { setErr(e?.response?.data?.detail || "Commit failed"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = filter.search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter.view === "accepted" && !r.accepted) return false;
      if (filter.view === "needs_review" && !(r.needs_review || r.retain_for_review)) return false;
      if (filter.view === "excluded" && r.kpi_id !== null) return false;
      if (filter.view === "auto_acceptable" && !(r.accepted && !r.retain_for_review)) return false;
      if (filter.view === "ai_assisted" && !r.ai_assisted) return false;
      if (filter.view === "sites_recognised" && !r.entity_id) return false;
      if (filter.kpi && r.kpi_id !== filter.kpi) return false;
      if (filter.band && CONF_BAND(r.confidence) !== filter.band) return false;
      if (filter.onlyAccepted && !r.accepted) return false;
      if (q) {
        const hay = (r.csv_activity_type + " " + r.site_name + " " + (r.supplier_or_vendor || "")).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, filter]);

  const setRow = (idx, patch) => setRows((rs) => rs.map((r) => r.row_index === idx ? { ...r, ...patch } : r));
  const toggleAll = (accepted) => setRows((rs) => rs.map((r) => r.kpi_id ? { ...r, accepted } : r));
  const removeRow = (idx) => setRows((rs) => rs.filter((r) => r.row_index !== idx));
  const retainForReview = (idx) => setRow(idx, { retain_for_review: true, accepted: true });
  const unretain = (idx) => setRow(idx, { retain_for_review: false });

  const setView = (view) => setFilter((f) => ({ ...f, view: f.view === view ? "all" : view, kpi: "", band: "" }));

  const acceptedCount = rows.filter((r) => r.accepted).length;
  const retainedCount = rows.filter((r) => r.retain_for_review).length;

  return (
    <div className="p-6 space-y-5" data-testid="upload-root">
      <div>
        <div className="section-label">Data Ingestion</div>
        <h1 className="text-2xl font-bold tracking-tight mt-1 flex items-center gap-2">
          <Bot className="text-brand"/> Bulk Activity Upload with AI Mapping
        </h1>
        <p className="text-sm text-ink-soft max-w-3xl">
          Upload your business-activity CSV (electricity bills, fuel logs, travel manifests, etc.).
          The AI mapper inspects each row's activity type, scope hint and source system, and assigns
          the matching KPI + emission factor. Review the suggestions, edit any mismatches, then
          commit — dashboards, KPI detail pages, GHG inventory and reports refresh automatically.
        </p>
      </div>

      <Stepper step={step}/>

      {step === 0 && (
        <div className="card p-6 max-w-3xl" data-testid="upload-step">
          <div className="section-label">1. Select your activity CSV</div>
          <p className="text-xs text-ink-soft mt-1">
            Required columns: <span className="font-mono">activity_type</span>, <span className="font-mono">activity_value</span>, <span className="font-mono">activity_unit</span>,
            <span className="font-mono"> period</span> (YYYY-MM) or <span className="font-mono">period_start</span>.
            Optional but recommended: <span className="font-mono">ghg_scope_hint</span>, <span className="font-mono">ghg_category_hint</span>,
            <span className="font-mono"> site_name</span>, <span className="font-mono">data_quality_score</span>, <span className="font-mono">supplier_or_vendor</span>, <span className="font-mono">evidence_reference</span>.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <input
              data-testid="file-input"
              type="file" accept=".csv,text/csv"
              onChange={(e) => setFile(e.target.files[0])}
              className="text-sm"
            />
            {file && <span className="text-xs text-ink-soft font-mono">{file.name} · {(file.size / 1024).toFixed(1)} KB</span>}
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm" data-testid="use-ai-label">
            <input data-testid="use-ai" type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)}/>
            <Sparkles size={12} className="text-brand"/>
            <span>Use Claude Sonnet 4.5 to assist with ambiguous rows (slower, more accurate)</span>
          </label>
          <div className="mt-5 flex gap-2">
            <button data-testid="run-mapping" disabled={!file || busy} onClick={onUpload} className="btn btn-primary">
              {busy ? "Mapping…" : (<><UpIcon size={14}/> Run AI Mapping</>)}
            </button>
          </div>
          {err && <div className="text-status-error text-sm mt-3" data-testid="error">{err}</div>}
        </div>
      )}

      {step === 1 && preview && (
        <>
          {/* Summary tiles — click to filter */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="preview-summary">
            <Tile label="Total rows"         value={preview.count}                                  view="all"             active={filter.view} onClick={setView}/>
            <Tile label="Auto-acceptable"    value={preview.summary.auto_acceptable}                view="auto_acceptable" color="#10B981" active={filter.view} onClick={setView}/>
            <Tile label="Needs review"       value={preview.summary.needs_review + retainedCount}   view="needs_review"    color="#F59E0B" active={filter.view} onClick={setView}/>
            <Tile label="Excluded"           value={preview.summary.by_confidence.excluded}         view="excluded"        color="#94A3B8" active={filter.view} onClick={setView}/>
            <Tile label="Sites recognised"   value={`${preview.sites_recognised}/${preview.count}`} view="sites_recognised" color="#3B82F6" active={filter.view} onClick={setView}/>
            <Tile label="AI-assisted"        value={preview.summary.ai_assisted}                    view="ai_assisted"     color="#A855F7" active={filter.view} onClick={setView}/>
          </div>
          {filter.view !== "all" && (
            <div className="text-xs text-ink-soft flex items-center gap-2" data-testid="filter-active-banner">
              <span>Filtered view: <span className="font-semibold">{filter.view.replaceAll("_"," ")}</span></span>
              <button data-testid="clear-view" onClick={() => setView("all")} className="btn btn-ghost text-xs"><X size={10}/> Clear</button>
            </div>
          )}

          {/* Per-KPI bar */}
          <div className="card p-4" data-testid="kpi-summary">
            <div className="section-label">Rows grouped by mapped KPI</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
              {Object.entries(preview.summary.by_kpi).map(([kpi, n]) => (
                <button
                  key={kpi}
                  data-testid={`pill-${kpi}`}
                  onClick={() => setFilter({ ...filter, kpi: filter.kpi === kpi ? "" : kpi })}
                  className={"card-soft p-3 text-left " + (filter.kpi === kpi ? "border-brand ring-2 ring-brand/20" : "")}
                >
                  <div className="text-[11px] text-ink-soft uppercase tracking-wider">{kpi}</div>
                  <div className="font-mono text-lg mt-0.5">{n}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Filter bar */}
          <div className="card p-3 flex items-center gap-2 flex-wrap" data-testid="filter-bar">
            <Filter size={12} className="text-ink-soft"/>
            <input
              data-testid="search-input"
              placeholder="Search activity, site, supplier…"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="border border-border rounded-md px-3 py-1.5 text-sm flex-1 min-w-[200px]"
            />
            <select data-testid="band-filter" value={filter.band} onChange={(e) => setFilter({ ...filter, band: e.target.value })}
              className="border border-border rounded-md px-2 py-1.5 text-sm">
              <option value="">All confidence</option>
              <option value="high">High (≥85%)</option>
              <option value="medium">Medium (65–85%)</option>
              <option value="low">Low (&lt;65%)</option>
            </select>
            <label className="text-xs inline-flex items-center gap-1">
              <input data-testid="only-accepted" type="checkbox" checked={filter.onlyAccepted}
                onChange={(e) => setFilter({ ...filter, onlyAccepted: e.target.checked })}/>
              Only accepted
            </label>
            <button data-testid="select-all" onClick={() => toggleAll(true)} className="btn btn-ghost text-xs">Accept all mapped</button>
            <button data-testid="select-none" onClick={() => toggleAll(false)} className="btn btn-ghost text-xs">Clear all</button>
            <button data-testid="ai-reassist" onClick={requestAiAssist} disabled={busy} className="btn btn-secondary text-xs">
              {busy ? <RefreshCcw size={11} className="animate-spin"/> : <Sparkles size={11}/>} Re-run with AI
            </button>
            <span className="text-xs text-ink-soft ml-auto">{filtered.length} shown · {acceptedCount} accepted</span>
          </div>

          {/* Mapping table */}
          <div className="card overflow-hidden" data-testid="mapping-table">
            <div className="max-h-[560px] overflow-auto">
              <table className="dense">
                <thead className="sticky top-0 bg-white z-10">
                  <tr>
                    <th></th>
                    <th>CSV activity</th>
                    <th>Site</th>
                    <th>Period</th>
                    <th className="text-right">Value</th>
                    <th>→ KPI</th>
                    <th>→ Activity type</th>
                    <th className="text-right">tCO₂e</th>
                    <th>Confidence</th>
                    <th>Accept</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => {
                    const open = expanded === r.row_index;
                    return (
                      <React.Fragment key={r.row_index}>
                        <tr data-testid={`row-${r.row_index}`} className={
                          "hover:bg-surface/60 " +
                          (r.kpi_id ? "" : "opacity-60 ") +
                          (r.retain_for_review ? "bg-amber-50/50 " : "")
                        }>
                          <td>
                            <button onClick={() => setExpanded(open ? null : r.row_index)} className="text-ink-soft hover:text-brand">
                              {open ? <ChevronUp size={11}/> : <ChevronDown size={11}/>}
                            </button>
                          </td>
                          <td>
                            <div className="font-medium">{r.csv_activity_type || "—"}</div>
                            <div className="text-[10px] text-ink-soft">{r.activity_domain}</div>
                          </td>
                          <td>
                            <div className="text-[11px]">{r.site_name}</div>
                            {r.entity_id ? (
                              <span className="dot bg-emerald-500" title="Matched to tenant Site entity"/>
                            ) : (
                              <span className="text-[10px] text-status-warn">untagged</span>
                            )}
                          </td>
                          <td className="text-[11px] font-mono">{r.period || "—"}</td>
                          <td className="num">{fmtNumber(r.consumption_value)} <span className="text-[10px] text-ink-soft">{r.consumption_unit}</span></td>
                          <td>
                            {r.kpi_id ? (
                              <select
                                data-testid={`kpi-select-${r.row_index}`}
                                value={r.kpi_id}
                                onChange={(e) => setRow(r.row_index, { kpi_id: e.target.value })}
                                className="border border-border rounded px-1 py-0.5 text-[11px] font-mono"
                              >
                                {Object.keys(catalogue?.kpi_to_activities || {}).map((k) => (
                                  <option key={k} value={k}>{k}</option>
                                ))}
                              </select>
                            ) : (
                              <span className="pill pill-error text-[10px]">unmapped</span>
                            )}
                          </td>
                          <td>
                            {r.activity_type ? (
                              <select
                                data-testid={`act-select-${r.row_index}`}
                                value={r.activity_type}
                                onChange={(e) => {
                                  const newAct = e.target.value;
                                  const meta = catalogue?.activities?.find((a) => a.activity_type === newAct);
                                  setRow(r.row_index, {
                                    activity_type: newAct,
                                    factor: meta?.default_factor ?? r.factor,
                                    factor_unit: meta?.factor_unit || r.factor_unit,
                                    factor_source: meta?.default_source || r.factor_source,
                                    co2e_value: meta?.default_factor ? round4((r.consumption_value * meta.default_factor) / 1000) : r.co2e_value,
                                  });
                                }}
                                className="border border-border rounded px-1 py-0.5 text-[11px] font-mono"
                              >
                                {(catalogue?.kpi_to_activities?.[r.kpi_id] || []).map((a) => (
                                  <option key={a} value={a}>{a}</option>
                                ))}
                              </select>
                            ) : <span className="text-[10px] text-ink-soft">—</span>}
                          </td>
                          <td className="num font-semibold">{fmtNumber(r.co2e_value, 3)}</td>
                          <td>
                            {r.kpi_id ? (
                              <span className={"pill " + BAND_PILL[CONF_BAND(r.confidence)] + " text-[10px]"} data-testid={`conf-${r.row_index}`}>
                                {(r.confidence * 100).toFixed(0)}%{r.ai_assisted ? " AI" : ""}
                              </span>
                            ) : (
                              <span className="text-[10px] text-ink-soft">—</span>
                            )}
                          </td>
                          <td>
                            {r.kpi_id ? (
                              <input
                                data-testid={`accept-${r.row_index}`}
                                type="checkbox"
                                checked={!!r.accepted}
                                onChange={(e) => setRow(r.row_index, { accepted: e.target.checked })}
                              />
                            ) : null}
                          </td>
                          <td className="whitespace-nowrap">
                            {r.retain_for_review ? (
                              <button
                                data-testid={`unretain-${r.row_index}`}
                                onClick={() => unretain(r.row_index)}
                                title="Unmark — return to normal flow"
                                className="pill pill-warn text-[10px] font-semibold"
                              >
                                ⚑ Retained
                              </button>
                            ) : (
                              <button
                                data-testid={`retain-${r.row_index}`}
                                onClick={() => retainForReview(r.row_index)}
                                title="Retain when committed — commit as 'needs_review' for further intervention"
                                className="text-ink-soft hover:text-amber-600 p-1"
                              >
                                <Flag size={12}/>
                              </button>
                            )}
                            <button
                              data-testid={`remove-${r.row_index}`}
                              onClick={() => removeRow(r.row_index)}
                              title="Completely remove from list (skip commit)"
                              className="text-ink-soft hover:text-status-error p-1 ml-1"
                            >
                              <Trash2 size={12}/>
                            </button>
                          </td>
                        </tr>
                        {open && (
                          <tr data-testid={`row-detail-${r.row_index}`}>
                            <td colSpan={11} className="bg-surface/40 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                <Detail label="Source CSV signals">
                                  <div>scope_hint: <span className="font-mono">{r.ghg_scope_hint || "—"}</span></div>
                                  <div>category_hint: <span className="font-mono">{r.ghg_category_hint || "—"}</span></div>
                                  <div>data_quality: <span className="font-mono">{r.data_quality_score}</span></div>
                                  <div>capture: <span className="font-mono">{r.data_capture_method || "—"}</span></div>
                                </Detail>
                                <Detail label="Provenance">
                                  <div>supplier: {r.supplier_or_vendor || "—"}</div>
                                  <div>meter/asset: <span className="font-mono">{r.meter_or_asset_id || "—"}</span></div>
                                  <div>evidence: <span className="font-mono">{r.evidence_reference || "—"}</span></div>
                                  <div>ext id: <span className="font-mono">{r.external_record_id || "—"}</span></div>
                                </Detail>
                                <Detail label="Calculation">
                                  <div className="font-mono">
                                    {fmtNumber(r.consumption_value)} {r.consumption_unit} × {r.factor ?? "—"} {r.factor_unit || ""} ÷ 1000 = <span className="font-semibold">{fmtNumber(r.co2e_value, 3)} tCO₂e</span>
                                  </div>
                                  <div className="mt-1">factor source: {r.factor_source || "—"}</div>
                                  <div className="mt-1 italic text-ink-soft">{r.rationale}</div>
                                </Detail>
                              </div>
                              {r.notes && <div className="mt-2 text-[11px] text-ink-soft italic">"{r.notes}"</div>}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {err && <div className="text-status-error text-sm" data-testid="error">{err}</div>}
          <div className="flex justify-between items-start flex-wrap gap-2" data-testid="step-actions">
            <button data-testid="back-step" onClick={() => { setStep(0); setPreview(null); setRows([]); }} className="btn btn-ghost">Back</button>
            <div className="flex flex-col items-end gap-1">
              <div className="text-sm text-ink-soft">
                {acceptedCount} of {rows.length} rows will be committed
                {retainedCount > 0 && (
                  <span className="ml-2 pill pill-warn text-[10px]" data-testid="retained-count">
                    {retainedCount} retained as needs_review
                  </span>
                )}
              </div>
              <button data-testid="commit" disabled={busy || acceptedCount === 0} onClick={commit} className="btn btn-primary">
                {busy ? "Committing…" : (<><Database size={14}/> Commit {acceptedCount} rows</>)}
              </button>
            </div>
          </div>
        </>
      )}

      {step === 2 && committed && (
        <div className="card p-6 max-w-3xl" data-testid="result">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="text-status-success"/></div>
            <div>
              <div className="section-label text-status-success">Committed</div>
              <h2 className="text-lg font-semibold mt-0.5">{committed.committed} activity rows added</h2>
            </div>
          </div>
          <div className="mt-4">
            <div className="dlabel">KPIs re-aggregated</div>
            <table className="dense w-full mt-2">
              <thead><tr><th>KPI</th><th>Period</th><th className="text-right">New total tCO₂e</th></tr></thead>
              <tbody>
                {committed.rebuilt.map((r) => (
                  <tr key={`${r.kpi_id}-${r.period}`}>
                    <td className="font-mono text-[11px]">{r.kpi_id}</td>
                    <td className="text-[11px]">{r.period}</td>
                    <td className="num font-semibold">{fmtNumber(r.new_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 flex gap-2">
            <a href="/" className="btn btn-primary">View Dashboard</a>
            <a href="/scorecard" className="btn btn-secondary">View Scorecard</a>
            <button data-testid="upload-another" onClick={() => { setStep(0); setFile(null); setPreview(null); setRows([]); setCommitted(null); }} className="btn btn-ghost">Upload another</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="stepper">
      {STEPS.map((s, idx) => {
        const active = step === idx;
        const done = step > idx;
        return (
          <div key={s} className={"card p-2 flex items-center gap-2 " + (active ? "border-brand ring-2 ring-brand/20" : "")}>
            <div className={"h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold " +
              (done ? "bg-emerald-100 text-status-success" : active ? "bg-brand text-white" : "bg-surface text-ink-soft")}>
              {done ? <Check size={12}/> : idx + 1}
            </div>
            <span className="text-sm">{s}</span>
          </div>
        );
      })}
    </div>
  );
}

function Tile({ label, value, color, view, active, onClick }) {
  const clickable = !!view;
  const isActive = active === view && view !== "all" || (view === "all" && active === "all");
  return (
    <button
      onClick={clickable ? () => onClick(view) : undefined}
      disabled={!clickable}
      data-testid={`tile-${label.toLowerCase().replace(/\s+/g,"-")}`}
      className={"card p-3 text-left transition " +
        (clickable ? "cursor-pointer hover:border-brand-200 " : "cursor-default ") +
        (isActive ? "border-brand ring-2 ring-brand/20" : "")}
    >
      <div className="flex items-center gap-2">
        {color && <span className="dot" style={{background: color}}/>}
        <span className="dlabel">{label}</span>
      </div>
      <div className="metric-value mt-0.5">{value}</div>
    </button>
  );
}

function Detail({ label, children }) {
  return (
    <div>
      <div className="dlabel">{label}</div>
      <div className="mt-1 space-y-0.5 text-[11px]">{children}</div>
    </div>
  );
}

function round4(n) { return Math.round(n * 10000) / 10000; }
